<template>
  <div>
    <b-card>
      <template slot="header">
        <b-card-title>
          <strong>{{title}}</strong>
          <p-button type="success" round size="sm" class="pull-right" v-if="!cardExists && !editingCard" @click.native="onAddCard">Add Card</p-button>
        </b-card-title>
        <p class="card-category mt-4">{{subTitle}}</p>
        <hr />
      </template>
      <template slot="default">
        <b-row>
          <b-col>
            <template v-if="!editingCard">
              <p class="mt-3 mb-4 text-center text-muted" v-if="!cardExists">No Payment Methods</p>
              <div class="card-brief-info d-flex align-items-center" v-else>
                <div class="card-brand-image mr-2">
                  <img :src="cardBriefInfo.cardImage" />
                </div>
                <div class="card-details flex-grow-1">
                  <div><strong>{{cardBriefInfo.brand}}</strong> ending in <span>{{cardBriefInfo.last4}}</span></div>
                  <div>Expires {{cardBriefInfo.expiresIn}}</div>
                </div>
                <p-button type="default" simple @click.native="onEditCard"><i class="ti-pencil" /> Edit</p-button>
              </div>
            </template>
            <template v-else>
              <b-form @submit="onSubmit" @reset="onReset" class="credit-card-inputs" :class="{ complete }" v-show="editingCard">
                <b-form-row>
                  <b-col>
                    <b-form-group label="Card Number">
                      <card-number class="form-control card-number"
                        ref="cardNumber"
                        :stripe="stripe"
                        :options="stripeOptions"
                        @change="number = $event.complete"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>
                <b-form-row>
                  <b-col>
                    <b-form-group label="Expiration Date">
                      <card-expiry class="form-control card-expiry"
                        ref="cardExpiry"
                        :stripe="stripe"
                        :options="stripeOptions"
                        @change="expiry = $event.complete"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <b-form-group label="CVC">
                      <card-cvc class="form-control card-cvc"
                        ref="cardCvc"
                        :stripe="stripe"
                        :options="stripeOptions"
                        @change="cvc = $event.complete"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>
                <b-form-row>
                  <b-col>
                    <b-form-group label="Card Holder Name">
                      <b-form-input
                        ref="cardName"
                        v-model="cardName"
                        placeholder=""
                        class="StripeElement"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>
                <b-form-row>
                  <b-col>
                    <p-button type="success" round size="sm" class="pull-right" :disabled="!complete || processing" @click.native="onSubmitCard" :loading="processing">Submit</p-button>
                    <p-button type="default" round size="sm" class="pull-right mr-2" @click.native="onCancelCard">Cancel</p-button>
                    <span class="pull-right mr-4 error text-danger" v-if="error">{{error}}</span>
                  </b-col>
                </b-form-row>
              </b-form>
            </template>
          </b-col>
        </b-row>
        <b-loading :loading="stripeCardLoading || processing" />
      </template>
    </b-card>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { Card, createToken, CardNumber, CardExpiry, CardCvc, PostalCode } from 'vue-stripe-elements-plus'
import moment from 'moment'
// import Card from './Card'
import { STRIPE_PUB_KEY } from '../../lib/stripe'

const cardBrandImages = {
  'American Express': require('@/assets/img/cards/AmericanExpress.png'),
  'MasterCard': require('@/assets/img/cards/Mastercard.png'),
  'Discover': require('@/assets/img/cards/Discover.png'),
  'JCB': require('@/assets/img/cards/JCB.png'),
  'Visa': require('@/assets/img/cards/Visa.png'),
  'Default': require('@/assets/img/cards/Default.png'),
}

export default {
  data() {
    return {
      editingCard: false,
      mounted: false,
      title: "Payment Methods",
      subTitle: "Enter your payment information to keep generating receipts when your apps get popular.",
      showPaymentMethodModal: false,
      modalTitle: 'Add Payment Method',
      stripe: STRIPE_PUB_KEY,
      stripeOptions: {
      },
      complete: false,
      number: false,
      expiry: false,
      cvc: false,
      cardName: '',
      processing: false,
    }
  },
  computed: {
    ...mapGetters([
      'error',
      'loading',
      'stripeCard',
      'stripeCustomer',
      'stripeLoading',
      'stripeCardLoading',
    ]),
    cardExists: function() {
      return !!this.stripeCard
    },
    cardBriefInfo: function() {
      if (!this.cardExists) return {}
      return {
        name: this.stripeCard.name,
        brand: this.stripeCard.brand,
        cardImage: cardBrandImages[this.stripeCard.brand] || cardBrandImages['Default'],
        last4: this.stripeCard.last4,
        expiresIn: moment().year(this.stripeCard.exp_year).month(this.stripeCard.exp_month-1).format('MM/YY'),
      }
    }
  },
  watch: {
    number () { this.update() },
    expiry () { this.update() },
    cvc () { this.update() }
  },
  methods: {
    update () {
      this.complete = this.number && this.expiry && this.cvc
      if (this.number) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus()
        } else if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.cardName) {
          this.$refs.cardName.focus()
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.number) {
          this.$refs.cardNumber.focus()
        }
      }
    },
    resetCardFields() {
      this.number = false
      this.expiry = false
      this.cvc = false
      this.cardName = ''
      this.complete = false
    },
    onAddCard() {
      this.editingCard = true
      this.resetCardFields()
    },
    onEditCard() {
      this.editingCard = true
      this.resetCardFields()
    },
    onSubmitCard() {
      this.processing = true
      createToken({
        name: this.cardName
      }).then(stripeTokenSource => {
        this.$store.dispatch('savePaymentMethod', {
          customer_id: this.stripeCustomer.id,
          ...stripeTokenSource,
        })
        .then(({card: stripeCard, customer: stripeCustomer}) => {
          this.processing = false
          this.editingCard = false
        })
        .catch((error) => {
          this.processing = false
        })
      }).catch((error) => {
        this.processing = false
      })
    },
    onCancelCard() {
      this.editingCard = false
    },
    onSubmit() {
      this.onSubmitCard()
    },
    onReset() {
      this.resetCardFields()
    }
  },
  components: {
    CardNumber,
    CardExpiry,
    CardCvc
  }
}
</script>
<style lang="scss">
  .credit-card-inputs {
    .form-control {
      border: solid 1px #ccc;
    }
  }
  .card-brief-info {
    .card-brand-image {}
    .card-details {
      font-size: 12px;
      line-height: 1.2;
      color: #6a6a6a;
      strong {
        color: #1a1a1a;
      }
    }
  }
</style>
